import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Modal,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Tabs,
  Tab,
  OverlayTrigger,
  Popover,
  Tooltip,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
// Import Button directly from react-bootstrap to avoid conflicts
import Button from 'react-bootstrap/Button';
import { handleTitleCaseChange } from '../utils/textUtils';
import { ValidationRules, validateForm, hasErrors, formatErrors } from '../utils/validation';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';

// ValidatedFormControl defined outside to prevent recreation on every render
const ValidatedFormControl = React.memo(
  ({
    name,
    label,
    required,
    children,
    tooltipDescription,
    hasAttemptedSubmit,
    validationErrors,
    formData,
    handleChange,
    ...props
  }) => {
    // Only show validation errors after first submit attempt
    const hasError = hasAttemptedSubmit && validationErrors[name];
    const popover = hasError ? (
      <Popover id={`popover-${name}`}>
        <Popover.Header as="h3">Validation Error</Popover.Header>
        <Popover.Body>{validationErrors[name]}</Popover.Body>
      </Popover>
    ) : null;

    const tooltip = tooltipDescription ? (
      <Tooltip id={`tooltip-${name}`}>{tooltipDescription}</Tooltip>
    ) : null;

    const formControl = children || (
      <Form.Control
        {...props}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        isInvalid={hasError}
      />
    );

    const wrappedControl = hasError ? (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
        {formControl}
      </OverlayTrigger>
    ) : tooltipDescription ? (
      <OverlayTrigger placement="top" overlay={tooltip}>
        {formControl}
      </OverlayTrigger>
    ) : (
      formControl
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {wrappedControl}
      </Form.Group>
    );
  }
);

function CustomerModal({ customer, onClose, onSave }) {
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [depots, setDepots] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    account_no: '',
    line_1: '',
    line_2: '',
    line_3: '',
    town: '',
    county: '',
    postcode: '',
    country: '',
    telephone: '',
    fax: '',
    contact_name: '',
    contact_telephone: '',
    email: '',
    cash_ident: '',
    type: '',
    size: '',
    how_heard_of_us: '',
    alert_text: '',
    company_type: '',
    website: '',
    notes: '',
    on_hold: false,
    company_reg_no: '',
    company_vat_no: '',
    company_cis_reg_no: '',
    default_hire_period: '',
    prop1: '',
    prop2: '',
    prop3: '',
    prop4: '',
    prop5: '',
    prop6: '',
    prop7: '',
    prop8: '',
    prop9: '',
    prop10: '',
    depot: '',
    tax_code: '',
    parent_acc_no: '',
    parent_id: '',
    is_internal: false,
    alt_name: '',
    active: true,
  });
  const [postcodeLoading, setPostcodeLoading] = useState(false);

  // Scroll to top of modal when active tab changes (to see validation errors)
  useEffect(() => {
    if (hasAttemptedSubmit && Object.keys(validationErrors).length > 0) {
      // Small delay to ensure tab content is rendered
      setTimeout(() => {
        const modalBody = document.querySelector('.modal-body');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
      }, 100);
    }
  }, [activeTab, hasAttemptedSubmit, validationErrors]);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        account_no: customer.account_no || '',
        line_1: customer.line_1 || '',
        line_2: customer.line_2 || '',
        line_3: customer.line_3 || '',
        town: customer.town || '',
        county: customer.county || '',
        postcode: customer.postcode || '',
        country: customer.country || '',
        telephone: customer.telephone || '',
        fax: customer.fax || '',
        contact_name: customer.contact_name || '',
        contact_telephone: customer.contact_telephone || '',
        email: customer.email || '',
        cash_ident: customer.cash_ident || '',
        type: customer.type || '',
        size: customer.size || '',
        how_heard_of_us: customer.how_heard_of_us || '',
        alert_text: customer.alert_text || '',
        company_type: customer.company_type || '',
        website: customer.website || '',
        notes: customer.notes || '',
        on_hold: customer.on_hold || false,
        company_reg_no: customer.company_reg_no || '',
        company_vat_no: customer.company_vat_no || '',
        company_cis_reg_no: customer.company_cis_reg_no || '',
        default_hire_period: customer.default_hire_period || '',
        prop1: customer.prop1 || '',
        prop2: customer.prop2 || '',
        prop3: customer.prop3 || '',
        prop4: customer.prop4 || '',
        prop5: customer.prop5 || '',
        prop6: customer.prop6 || '',
        prop7: customer.prop7 || '',
        prop8: customer.prop8 || '',
        prop9: customer.prop9 || '',
        prop10: customer.prop10 || '',
        depot: customer.depot || '',
        tax_code: customer.tax_code || '',
        parent_acc_no: customer.parent_acc_no || '',
        parent_id: customer.parent_id || '',
        is_internal: customer.is_internal || false,
        alt_name: customer.alt_name || '',
        active: customer.active !== undefined ? customer.active : true,
      });
    }
    fetchDepots();
  }, [customer]);

  const fetchDepots = async () => {
    try {
      const response = await axios.get('/api/depots?limit=1000');
      setDepots(response.data.depots || []);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  const titleCaseFields = [
    'name',
    'contact_name',
    'line_1',
    'line_2',
    'line_3',
    'town',
    'county',
    'country',
    'company_type',
    'alt_name',
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle account number capitalization
    if (name === 'account_no') {
      setFormData({
        ...formData,
        [name]: value.toUpperCase(),
      });

      // Clear errors only if they exist (avoid unnecessary re-renders)
      if (validationErrors[name] || modalError) {
        if (validationErrors[name]) {
          const newErrors = { ...validationErrors };
          delete newErrors[name];
          setValidationErrors(newErrors);
        }
        if (modalError) {
          setModalError('');
        }
      }
      return;
    }

    // Handle other fields with title case
    handleTitleCaseChange(e, setFormData, formData, titleCaseFields);

    // Clear errors after updating form data (avoid unnecessary re-renders)
    if (validationErrors[name] || modalError) {
      if (validationErrors[name]) {
        const newErrors = { ...validationErrors };
        delete newErrors[name];
        setValidationErrors(newErrors);
      }
      if (modalError) {
        setModalError('');
      }
    }
  };

  const handlePostcodeLookup = async () => {
    if (!formData.postcode || formData.postcode.trim() === '') {
      setModalError('Please enter a postcode first');
      return;
    }

    setPostcodeLoading(true);
    try {
      // Using postcodes.io - free UK postcode API
      const response = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(formData.postcode.replace(/\s/g, ''))}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === 200 && data.result) {
          setFormData({
            ...formData,
            town: data.result.admin_district || formData.town,
            county: data.result.admin_county || formData.county,
            country: 'United Kingdom',
          });
          setModalError('');
        } else {
          setModalError('Postcode not found. Please check the postcode and try again.');
        }
      } else {
        setModalError('Unable to lookup postcode. Please enter address details manually.');
      }
    } catch (error) {
      console.error('Postcode lookup error:', error);
      setModalError('Postcode lookup service unavailable. Please enter address details manually.');
    } finally {
      setPostcodeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    setHasAttemptedSubmit(true);

    console.log('Form submitted, validating data:', formData);

    // Comprehensive validation using validation rules
    const errors = validateForm(formData, ValidationRules.customer);
    console.log('Validation errors:', errors);
    console.log('Has errors?', hasErrors(errors));

    setValidationErrors(errors);

    if (hasErrors(errors)) {
      const errorMessage = formatErrors(errors);
      console.log('Formatted error message:', errorMessage);
      setModalError(errorMessage);

      // Show popup alert with validation errors
      console.log('About to show alert...');
      window.alert('⚠️ Validation Error\n\n' + errorMessage);
      console.log('Alert shown');

      // Switch to the appropriate tab if there are validation errors
      // Check address fields first since line_1 is now required
      if (
        errors.line_1 ||
        errors.line_2 ||
        errors.line_3 ||
        errors.town ||
        errors.county ||
        errors.postcode ||
        errors.country
      ) {
        console.log('Switching to address tab due to validation errors:', errors);
        setActiveTab('address');
      } else if (
        errors.telephone ||
        errors.fax ||
        errors.email ||
        errors.contact_name ||
        errors.contact_telephone
      ) {
        console.log('Switching to contact tab due to validation errors:', errors);
        setActiveTab('contact');
      } else if (
        errors.company_reg_no ||
        errors.company_vat_no ||
        errors.company_cis_reg_no ||
        errors.tax_code
      ) {
        console.log('Switching to company tab due to validation errors:', errors);
        setActiveTab('company');
      } else if (errors.notes || errors.alert_text) {
        console.log('Switching to additional tab due to validation errors:', errors);
        setActiveTab('additional');
      } else {
        // If error is on basic tab, stay on basic
        console.log('Staying on current tab, errors:', errors);
      }

      return;
    }

    // Website validation and auto-correction
    let finalFormData = { ...formData };
    if (finalFormData.website && finalFormData.website.trim() !== '') {
      const websiteRegex = /^https?:\/\/.+/;
      if (!websiteRegex.test(finalFormData.website)) {
        // Auto-add http:// if not present
        finalFormData.website = 'http://' + finalFormData.website;
      }
    }

    try {
      await onSave(finalFormData);
    } catch (error) {
      // Display server-side errors in the modal and as popup
      const serverError = error.response?.data?.error || 'Failed to save customer';
      setModalError(serverError);
      alert('⚠️ Error Saving Customer\n\n' + serverError);

      // If it's a line_1 error from server, switch to address tab
      if (
        serverError.toLowerCase().includes('address line 1') ||
        serverError.toLowerCase().includes('line_1')
      ) {
        setActiveTab('address');
      }
    }
  };

  const handleCopy = () => {
    const copiedData = { ...formData };
    delete copiedData.id;
    copiedData.account_no = copiedData.account_no + '_copy';
    copiedData.name = copiedData.name + ' (Copy)';
    setFormData(copiedData);
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{customer ? 'Edit Customer' : 'Add New Customer'}</Modal.Title>
        {customer && (
          <Button variant="secondary" onClick={handleCopy} className="ms-2">
            Copy Row
          </Button>
        )}
      </Modal.Header>

      {modalError && (
        <div className="alert alert-danger mx-3 mt-3 mb-0" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {modalError}
        </div>
      )}
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => k && setActiveTab(k)}
            id="customer-tabs"
            className="mb-3"
          >
            {/* Basic Information Tab */}
            <Tab eventKey="basic" title="Basic Info">
              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="name"
                    label="Name"
                    required={true}
                    type="text"
                    tooltipDescription="Customer name used in contracts, invoices, and reports"
                    hasAttemptedSubmit={hasAttemptedSubmit}
                    validationErrors={validationErrors}
                    formData={formData}
                    handleChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account No</Form.Label>
                    <Form.Control
                      type="text"
                      name="account_no"
                      value={formData.account_no || ''}
                      onChange={handleChange}
                      isInvalid={hasAttemptedSubmit && validationErrors.account_no}
                      placeholder="Enter account number"
                      maxLength={8}
                    />
                    <Form.Text className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Leave blank for 'Cash Customer'
                    </Form.Text>
                    {hasAttemptedSubmit && validationErrors.account_no && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.account_no}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Alt. Name</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('alt_name')}>
                          Alternative name for the customer
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="alt_name"
                        value={formData.alt_name}
                        onChange={handleChange}
                        {...createTooltipProps('alt_name')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="company_type"
                    label="Company Type"
                    type="text"
                    tooltipDescription="Type of company for business classification"
                    hasAttemptedSubmit={hasAttemptedSubmit}
                    validationErrors={validationErrors}
                    formData={formData}
                    handleChange={handleChange}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="type"
                    label="Type"
                    tooltipDescription="Customer type: Cash, Account, or Prospect"
                    hasAttemptedSubmit={hasAttemptedSubmit}
                    validationErrors={validationErrors}
                    formData={formData}
                    handleChange={handleChange}
                  >
                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      isInvalid={hasAttemptedSubmit && validationErrors.type}
                    >
                      <option value="">Select type...</option>
                      <MissingSelectOption
                        value={formData.type}
                        options={[{ value: 'C' }, { value: 'A' }, { value: 'P' }]}
                        getOptionValue={(option) => option.value}
                      />
                      <option value="C">C - Cash</option>
                      <option value="A">A - Account</option>
                      <option value="P">P - Prospect</option>
                    </Form.Select>
                  </ValidatedFormControl>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Size</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('size')}>
                          Customer size classification
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="size"
                        value={formData.size}
                        onChange={handleChange}
                        {...createTooltipProps('size')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>How Heard Of Us</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('how_heard_of_us')}>
                          Marketing source information
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="how_heard_of_us"
                        value={formData.how_heard_of_us}
                        onChange={handleChange}
                        {...createTooltipProps('how_heard_of_us')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cash Ident</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('cash_ident')}>
                          Cash customer identification
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="cash_ident"
                        value={formData.cash_ident}
                        onChange={handleChange}
                        {...createTooltipProps('cash_ident')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('active')}>
                          Controls whether customer is active
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="active"
                        label="Active"
                        checked={formData.active}
                        onChange={handleChange}
                        className="mb-3"
                        {...createTooltipProps('active')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('on_hold')}>
                          Places customer on hold status
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="on_hold"
                        label="On Hold"
                        checked={formData.on_hold}
                        onChange={handleChange}
                        className="mb-3"
                        {...createTooltipProps('on_hold')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('is_internal')}>
                          Indicates if customer is internal
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="is_internal"
                        label="Is Internal?"
                        checked={formData.is_internal}
                        onChange={handleChange}
                        className="mb-3"
                        {...createTooltipProps('is_internal')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Address Tab */}
            <Tab eventKey="address" title="Address">
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Line 1 <span className="text-danger">*</span>
                    </Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('line_1')}>
                          Primary address line (required)
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="line_1"
                        value={formData.line_1}
                        onChange={handleChange}
                        isInvalid={hasAttemptedSubmit && validationErrors.line_1}
                        {...createTooltipProps('line_1')}
                      />
                    </OverlayTrigger>
                    {hasAttemptedSubmit && validationErrors.line_1 && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.line_1}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Line 2</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('line_2')}>Secondary address line</Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="line_2"
                        value={formData.line_2}
                        onChange={handleChange}
                        {...createTooltipProps('line_2')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Line 3</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('line_3')}>Tertiary address line</Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="line_3"
                        value={formData.line_3}
                        onChange={handleChange}
                        {...createTooltipProps('line_3')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Town</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('town')}>Town or city name</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="town"
                        value={formData.town}
                        onChange={handleChange}
                        {...createTooltipProps('town')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>County</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('county')}>County or state</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="county"
                        value={formData.county}
                        onChange={handleChange}
                        {...createTooltipProps('county')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <div className="d-flex">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip {...createTooltipProps('postcode')}>Postal or ZIP code</Tooltip>
                        }
                      >
                        <Form.Control
                          type="text"
                          name="postcode"
                          value={formData.postcode}
                          onChange={handleChange}
                          className="me-2"
                          {...createTooltipProps('postcode')}
                        />
                      </OverlayTrigger>
                      <Button
                        variant="outline-primary"
                        onClick={handlePostcodeLookup}
                        disabled={postcodeLoading}
                      >
                        {postcodeLoading ? 'Looking up...' : 'Lookup'}
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Lookup is for UK addresses only
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('country')}>Country name</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        {...createTooltipProps('country')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Contact Tab */}
            <Tab eventKey="contact" title="Contact">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contact Name</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('contact_name')}>
                          Primary contact person name
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        {...createTooltipProps('contact_name')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contact Telephone</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('contact_telephone')}>
                          Primary contact telephone number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="contact_telephone"
                        value={formData.contact_telephone}
                        onChange={handleChange}
                        {...createTooltipProps('contact_telephone')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telephone</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('telephone')}>
                          Main business telephone number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        {...createTooltipProps('telephone')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fax</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('fax')}>Fax number</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="fax"
                        value={formData.fax}
                        onChange={handleChange}
                        {...createTooltipProps('fax')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('email')}>Primary email address</Tooltip>
                      }
                    >
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        {...createTooltipProps('email')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Website</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('website')}>Company website URL</Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        placeholder="http://www.example.com"
                        {...createTooltipProps('website')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Business Details Tab */}
            <Tab eventKey="business" title="Business">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Reg No</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('company_reg_no')}>
                          Company registration number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="company_reg_no"
                        value={formData.company_reg_no}
                        onChange={handleChange}
                        {...createTooltipProps('company_reg_no')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company VAT No</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('company_vat_no')}>
                          Company VAT registration number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="company_vat_no"
                        value={formData.company_vat_no}
                        onChange={handleChange}
                        {...createTooltipProps('company_vat_no')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company CIS Reg No</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('company_cis_reg_no')}>
                          Construction Industry Scheme registration
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="company_cis_reg_no"
                        value={formData.company_cis_reg_no}
                        onChange={handleChange}
                        {...createTooltipProps('company_cis_reg_no')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tax Code</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('tax_code')}>
                          Tax code for transactions
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="tax_code"
                        value={formData.tax_code}
                        onChange={handleChange}
                        {...createTooltipProps('tax_code')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Hire Period (days)</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('default_hire_period')}>
                          Default hire period in days
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="number"
                        name="default_hire_period"
                        value={formData.default_hire_period}
                        onChange={handleChange}
                        {...createTooltipProps('default_hire_period')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Depot</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('depot')}>
                          Used for availability checking, delivery planning, and equipment tracking
                        </Tooltip>
                      }
                    >
                      <Form.Select
                        name="depot"
                        value={formData.depot}
                        onChange={handleChange}
                        {...createTooltipProps('depot')}
                      >
                        <option value="">Select a depot...</option>
                        <MissingSelectOption
                          value={formData.depot}
                          options={depots}
                          getOptionValue={(depot) => depot.short_code}
                        />
                        {depots.map((depot) => (
                          <option key={depot.id} value={depot.short_code}>
                            {depot.depot_name}
                          </option>
                        ))}
                      </Form.Select>
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent Account No</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('parent_acc_no')}>
                          Parent account number for hierarchical relationships
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="parent_acc_no"
                        value={formData.parent_acc_no}
                        onChange={handleChange}
                        {...createTooltipProps('parent_acc_no')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent ID</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('parent_id')}>
                          Parent customer ID for hierarchical relationships
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="parent_id"
                        value={formData.parent_id}
                        onChange={handleChange}
                        {...createTooltipProps('parent_id')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Properties Tab */}
            <Tab eventKey="properties" title="Properties">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop1</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop1"
                      value={formData.prop1}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop2</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop2"
                      value={formData.prop2}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop3</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop3"
                      value={formData.prop3}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop4</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop4"
                      value={formData.prop4}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop5</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop5"
                      value={formData.prop5}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop6</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop6"
                      value={formData.prop6}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop7</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop7"
                      value={formData.prop7}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop8</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop8"
                      value={formData.prop8}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop9</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop9"
                      value={formData.prop9}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prop10</Form.Label>
                    <Form.Control
                      type="text"
                      name="prop10"
                      value={formData.prop10}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Notes Tab */}
            <Tab eventKey="notes" title="Notes">
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Alert Text</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('alert_text')}>
                          Alert text displayed for this customer
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="alert_text"
                        value={formData.alert_text}
                        onChange={handleChange}
                        {...createTooltipProps('alert_text')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('notes')}>
                          Additional notes about the customer
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        {...createTooltipProps('notes')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {customer ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function Customers() {
  console.log('Customers component is loading...');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Helper function to filter out false/0 values
  const displayValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === '0' ||
      value === 0 ||
      value === false ||
      value === 'false'
    ) {
      return '';
    }
    return value;
  };

  // Helper function to check if a value is truly enabled (not false, 0, '0', 'false', etc.)
  const isEnabled = (value) => {
    // More comprehensive check for truly enabled values
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 0 ||
      value === '0' ||
      value === false ||
      value === 'false'
    ) {
      return false;
    }
    // For strings, check if it's just whitespace
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }
    return true;
  };

  const fetchCustomers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/customers', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchCustomers(newPage, searchTerm);
  };

  const handleAddCustomer = () => {
    console.log('handleAddCustomer called');
    console.log('Current showModal state:', showModal);
    setEditingCustomer(null);
    setShowModal(true);
    console.log('showModal should now be true');
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      console.log('Saving customer data:', customerData);
      if (editingCustomer) {
        console.log('Updating customer ID:', editingCustomer.id);
        await axios.put(`/api/customers/${editingCustomer.id}`, customerData);
        setSuccess('Customer updated successfully');
      } else {
        console.log('Creating new customer');
        const response = await axios.post('/api/customers', customerData);
        console.log('Customer created successfully:', response.data);
        setSuccess('Customer created successfully');
      }
      setShowModal(false);
      fetchCustomers(pagination.page, searchTerm);
    } catch (error) {
      console.error('Error saving customer:', error);
      console.error('Error response:', error.response?.data);
      // Throw the error so the modal can catch and display it
      throw error;
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await axios.delete(`/api/customers/${id}`);
        setSuccess('Customer deleted successfully');
        fetchCustomers(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete customer');
      }
    }
  };

  const handleDeleteAllCustomers = async () => {
    console.log('Delete All button clicked');
    if (
      window.confirm('Are you sure you want to delete ALL customers? This action cannot be undone!')
    ) {
      if (
        window.confirm(
          'This will permanently delete all your customer records. Are you absolutely sure?'
        )
      ) {
        try {
          console.log('Attempting to delete all customers');
          const response = await axios.delete('/api/customers');
          console.log('Delete all response:', response.data);
          setSuccess(`All customers deleted successfully (${response.data.deletedCount} records)`);
          fetchCustomers(1, '');
        } catch (error) {
          console.error('Error deleting all customers:', error);
          setError('Failed to delete all customers');
        }
      }
    }
  };

  console.log('About to render Customers component');
  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-people-fill me-3 text-primary" style={{ fontSize: '2rem' }}></i>
                <div>
                  <h1 className="mb-0">Customers</h1>
                  <p className="text-muted mb-0">
                    Manage your customer database and contact information
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={() => {
                  console.log('Add Customer Button clicked - direct handler');
                  handleAddCustomer();
                }}
                className="me-2"
              >
                Add Customer
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAllCustomers}
                disabled={customers.length === 0}
              >
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by name, account no, contact name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading customers...</div>
          ) : customers.length === 0 ? (
            <div className="text-center p-3">No customers found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                  <thead>
                    <tr>
                      <th>Account No</th>
                      <th>Name</th>
                      <th>Contact Name</th>
                      <th>Email</th>
                      <th>Telephone</th>
                      <th className="d-none">Town</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id}>
                        <td>
                          <strong>{displayValue(customer.account_no)}</strong>
                        </td>
                        <td className="customer-name">{displayValue(customer.name)}</td>
                        <td className="contact-name">{displayValue(customer.contact_name)}</td>
                        <td>{displayValue(customer.email)}</td>
                        <td>{displayValue(customer.telephone)}</td>
                        <td className="d-none">{displayValue(customer.town)}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            <Badge bg={isEnabled(customer.active) ? 'success' : 'secondary'}>
                              {isEnabled(customer.active) ? 'Active' : 'Inactive'}
                            </Badge>
                            {isEnabled(customer.on_hold) && <Badge bg="warning">On Hold</Badge>}
                            {isEnabled(customer.is_internal) && <Badge bg="info">Internal</Badge>}
                            {isEnabled(customer.alert_text) && <Badge bg="danger">Alert</Badge>}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditCustomer(customer)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteCustomer(customer.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <CustomerModal
          customer={editingCustomer}
          onClose={() => setShowModal(false)}
          onSave={handleSaveCustomer}
        />
      )}
    </Container>
  );
}

export default Customers;
